import { GridsterItemConfig } from 'angular-gridster2';

import { DisplayType, FieldTypeEnum } from '@models/module/fields/enums';
import { ScreenFieldModel } from '@models/module/fields/main';
import { GridSearchEntityInstance } from '@models/module/grid';
import { DataCardSettingLabelDefault, DataCardSettingValueDefault } from '@models/module/main/dataCardSetting';
import { Currency } from '@models/system';
import {
  DATA_CARD_LIST_MAX_ROWS,
  DATA_CARD_LIST_MIN_ROWS,
  DATA_CARD_TABLE_MAX_ROWS,
  DATA_CARD_TABLE_MIN_ROWS,
  getDataCardFieldDefaultRows,
  getDataCardFieldMinCols,
  isDataCardFieldFullWidth,
} from '@module-instance/data-card/data-card-layout';
import { StatusColumnType } from '@shared/components/data-list/common';
import { parseTableFieldValue } from '@shared/utils';

export type DataCardViewPartValueType = string | number | boolean | StatusColumnType;

/** Normalized relation value: rendered as a plain label, or as a link when the target document ids are present. */
export type DataCardRelationValue = {
  label: string;
  entityInstanceId?: number;
  moduleId?: number;
  contractId?: number;
};

export class DataCardViewPart implements GridsterItemConfig {
  x: number;
  y: number;
  cols: number;
  rows: number;

  id: number | string;
  field: ScreenFieldModel;
  value?: DataCardViewPartValueType;
  preview?: boolean;
  currency?: Currency;

  constructor(id: number | string, field: ScreenFieldModel, preview: boolean = false) {
    this.id = id;
    this.field = field;
    this.field.labelFormatting = this.field.labelFormatting || { ...DataCardSettingLabelDefault };
    this.field.valueFormatting = this.field.valueFormatting || { ...DataCardSettingValueDefault };
    this.y = this.field.y || 0;
    /* Table/Weather values are forced full-width, starting at column 0, since a partial-width
       span starting mid-grid would overflow. Row count is only defaulted here (not forced), the
       actual read-only rendering uses `effectiveRows`, sized to the real content. */
    const isFullWidth = isDataCardFieldFullWidth(this.field.typeId);
    this.x = isFullWidth ? 0 : this.field.x || 0;
    this.cols = Math.max(this.field.cols || 6, getDataCardFieldMinCols(this.field.typeId));
    this.rows = this.field.rows || getDataCardFieldDefaultRows(this.field.typeId);

    this.preview = preview;
  }

  get valueOrPreview(): DataCardViewPartValueType {
    if (!this.preview) {
      return this.value;
    }
    if (this.isNumber) {
      return this.isNumberIndicator ? 121 : this.isNumberProgress ? 29 : 12345.678;
    } else if (this.isDate) {
      return '2022-11-29T13:49:22Z';
    } else if (this.isYesNo) {
      return true;
    } else if (this.isState) {
      return { label: 'Active', color: '#fff', bgColor: '#54b095' } as StatusColumnType;
    } else if (this.isWeather || this.isTable) {
      return '"Lorem ipsum", "Lorem ipsum", "Lorem ipsum"';
    } else if (this.isAttachment) {
      return 'document.pdf, image.png';
    }
    return 'Lorem ipsum';
  }

  get valueNumber(): number {
    const value = this.valueOrPreview as number;
    if (value && this.isNumberIndicator) {
      return Math.abs(value);
    }
    return value;
  }

  get valueString(): string {
    return this.valueOrPreview?.toString();
  }

  get valueBoolean(): boolean {
    return this.valueOrPreview as boolean;
  }

  get valueState(): StatusColumnType {
    return this.valueOrPreview as StatusColumnType;
  }

  get isNumber(): boolean {
    return this.field.typeId == FieldTypeEnum.Number;
  }

  get isDate(): boolean {
    return this.field.typeId == FieldTypeEnum.Date;
  }

  get isYesNo(): boolean {
    return this.field.typeId == FieldTypeEnum.YesNo;
  }

  get isState(): boolean {
    return this.field.displayTypeId == DisplayType.Status;
  }

  get isLine(): boolean {
    return this.field.displayTypeId == DisplayType.Line;
  }

  get isTable(): boolean {
    return this.field.typeId == FieldTypeEnum.Table;
  }

  get valueTableRows(): string[][] {
    return parseTableFieldValue(this.valueString);
  }

  get isAttachment(): boolean {
    return this.field.typeId == FieldTypeEnum.Attachment;
  }

  /** Grid data only carries attachment file names as a flattened, comma-separated string (no ids
   *  to open/download the file), so these render as plain names rather than the full attachment component. */
  get valueAttachmentNames(): string[] {
    return (this.valueString || '')
      .split(',')
      .map(name => name.trim())
      .filter(name => !!name);
  }

  get isRelation(): boolean {
    return this.field.typeId == FieldTypeEnum.Relation;
  }

  /** Best-effort normalization: the grid can return a relation as a plain label or as an object/array carrying the target document ids. */
  get valueRelations(): DataCardRelationValue[] {
    const value = this.value as unknown;
    if (value === null || value === undefined || value === '') {
      return [];
    }
    const items = Array.isArray(value) ? value : [value];
    return items.map(item => this.toRelationValue(item)).filter((item): item is DataCardRelationValue => !!item?.label);
  }

  get isWeather(): boolean {
    return this.field.typeId == FieldTypeEnum.Weather;
  }

  get isText(): boolean {
    return (
      !this.isNumber &&
      !this.isDate &&
      !this.isYesNo &&
      !this.isState &&
      !this.isLine &&
      !this.isTable &&
      !this.isAttachment &&
      !this.isRelation &&
      !this.isWeather
    );
  }

  /** Row span sized to the actual content for Table/Weather/Attachment/Relation (clamped), so an
   *  empty/small value doesn't leave a big gap and a large one scrolls instead of pushing everything
   *  else down. */
  get effectiveRows(): number {
    if (this.isTable || this.isWeather) {
      const dataRowCount = this.valueTableRows.length;
      if (!dataRowCount) {
        /* Nothing to render (no rows), so don't reserve any extra space beyond a normal field row. */
        return 1;
      }
      return Math.min(Math.max(dataRowCount + 1, DATA_CARD_TABLE_MIN_ROWS), DATA_CARD_TABLE_MAX_ROWS);
    }
    if (this.isAttachment || this.isRelation) {
      const itemCount = this.isAttachment ? this.valueAttachmentNames.length : this.valueRelations.length;
      if (!itemCount) {
        return 1;
      }
      return Math.min(Math.max(itemCount, DATA_CARD_LIST_MIN_ROWS), DATA_CARD_LIST_MAX_ROWS);
    }
    return this.rows;
  }

  private toRelationValue(item: unknown): DataCardRelationValue | undefined {
    if (item === null || item === undefined || item === '') {
      return undefined;
    }
    if (typeof item !== 'object') {
      return { label: `${item}` };
    }
    const relation = item as Record<string, unknown>;
    const label = (relation.label ?? relation.value ?? relation.fullDocNum ?? relation.full_doc_num ?? '').toString();
    if (!label) {
      return undefined;
    }
    const entityInstanceId = Number(relation.entityInstanceId ?? relation.entity_instance_id);
    const moduleId = Number(relation.moduleId ?? relation.module_id);
    const contractId = Number(relation.contractId ?? relation.contract_id);
    return {
      label,
      entityInstanceId: Number.isFinite(entityInstanceId) ? entityInstanceId : undefined,
      moduleId: Number.isFinite(moduleId) ? moduleId : undefined,
      contractId: Number.isFinite(contractId) ? contractId : undefined,
    };
  }

  get displayTypeId(): DisplayType {
    return this.field.displayTypeId || DisplayType.Text;
  }

  get isNumberIndicatorUp(): boolean {
    return (
      this.displayTypeId == DisplayType.IndicatorUp ||
      (this.displayTypeId == DisplayType.IndicatorBasedOnValue && this.valueNumber > 0)
    );
  }

  get isNumberIndicatorDown(): boolean {
    return (
      this.displayTypeId == DisplayType.IndicatorDown ||
      (this.displayTypeId == DisplayType.IndicatorBasedOnValue && this.valueNumber < 0)
    );
  }

  get isNumberIndicator(): boolean {
    return (
      this.displayTypeId == DisplayType.IndicatorUp ||
      this.displayTypeId == DisplayType.IndicatorDown ||
      this.displayTypeId == DisplayType.IndicatorBasedOnValue
    );
  }

  get isNumberDurationProgress(): boolean {
    return this.displayTypeId == DisplayType.PercentDurationProgress;
  }

  get isNumberAmountProgress(): boolean {
    return this.displayTypeId == DisplayType.PercentAmountProgress;
  }

  get isNumberProgress(): boolean {
    return this.isNumberDurationProgress || this.isNumberAmountProgress;
  }
}

export interface DataCard extends GridSearchEntityInstance {
  parts: DataCardViewPart[];
}
